import { z } from "zod";
import { oc } from "@orpc/contract";

export const VapiAssistantSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  firstMessage: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export type VapiAssistantSummary = z.infer<typeof VapiAssistantSummarySchema>;

export const VapiPhoneNumberSummarySchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  name: z.string().nullable(),
  status: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export type VapiPhoneNumberSummary = z.infer<
  typeof VapiPhoneNumberSummarySchema
>;

export const VapiVoiceSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  gender: z.string().nullable(),
  previewUrl: z.string().nullable(),
  provider: z.string().nullable(),
  language: z.string().nullable(),
  languageCode: z.string().nullable(),
  accent: z.string().nullable(),
});

export type VapiVoiceSummary = z.infer<typeof VapiVoiceSummarySchema>;

export const VapiAvailabilitySchema = z.object({
  configured: z.boolean(),
});

export type VapiAvailability = z.infer<typeof VapiAvailabilitySchema>;

export const CreateVapiAssistantInputSchema = z.object({
  name: z.string().min(1),
  voiceId: z.string().min(1).default("alloy"),
  systemPrompt: z.string().optional(),
  firstMessage: z.string().optional(),
});

export type CreateVapiAssistantInput = z.infer<
  typeof CreateVapiAssistantInputSchema
>;

export const CreateVapiPhoneNumberInputSchema = z.object({
  name: z.string().optional(),
  numberDesiredAreaCode: z
    .string()
    .regex(/^\d{3}$/, "Area code must be exactly 3 digits")
    .optional(),
});

export type CreateVapiPhoneNumberInput = z.infer<
  typeof CreateVapiPhoneNumberInputSchema
>;

const checkAvailability = oc.output(VapiAvailabilitySchema).route({
  summary: "Check voice agent provider availability",
  description:
    "Returns whether the voice agent provider's API key is configured on this server.",
  method: "GET",
});

const listAssistants = oc.output(z.array(VapiAssistantSummarySchema)).route({
  summary: "List voice assistants",
  description:
    "Returns the voice assistants in the server's voice provider account; empty when the provider is not configured.",
  method: "GET",
});

const createAssistant = oc
  .input(CreateVapiAssistantInputSchema)
  .output(VapiAssistantSummarySchema)
  .route({
    summary: "Create voice assistant",
    description:
      "Creates a voice assistant at the voice provider with a name, voice, optional system prompt and first message.",
  });

const listPhoneNumbers = oc
  .output(z.array(VapiPhoneNumberSummarySchema))
  .route({
    summary: "List provider phone numbers",
    description:
      "Returns phone numbers in the server's voice provider account; empty when the provider is not configured.",
    method: "GET",
  });

const createPhoneNumber = oc
  .input(CreateVapiPhoneNumberInputSchema)
  .output(VapiPhoneNumberSummarySchema)
  .route({
    summary: "Provision voice provider phone number",
    description:
      "Provisions a new phone number at the voice provider, optionally in a requested area code. Does not register it with the organization.",
  });

const deletePhoneNumber = oc
  .input(z.object({ id: z.string().min(1) }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Release provider phone number",
    description:
      "Deletes a phone number from the voice provider account by provider id.",
  });

const listVoices = oc.output(z.array(VapiVoiceSummarySchema)).route({
  summary: "List available assistant voices",
  description:
    "Returns voices offered by the voice provider with gender, language, accent and preview audio links.",
  method: "GET",
});

export const vapiContracts = {
  checkAvailability,
  listAssistants,
  createAssistant,
  listPhoneNumbers,
  createPhoneNumber,
  deletePhoneNumber,
  listVoices,
} as const;

export type VapiContracts = typeof vapiContracts;
